import React, { useState } from 'react'
import { Table as RsuiteTable } from 'rsuite'
import { TableProps, RowDataType, RowKeyType } from 'rsuite-table'
import classNames from 'classnames'
import {
  HeaderCell,
  Cell,
  // CRSCell,
  StatusCell,
  ActionCell,
  CheckCell,
  CheckHeaderCell,
  CustomCell
} from './cells'
import TableFooter from './footer/TableFooter'
import NoDataFallback from '../../atoms/noDataFallback/NoDataFallback'
import { renderTableColumns, TableColumnConfig } from './columnConfig'
import './Table.scss'

export type { TableColumnConfig, TableActionConfig } from './columnConfig'
export { DEFAULT_COLUMN_MIN_WIDTH } from './columnConfig'

const { Column } = RsuiteTable

export interface Pagination {
  page: number;
  limit: number;
}

const DEFAULT_PAGE_SIZE = 10
function Table<R extends RowDataType<any>, K extends RowKeyType>(
  props: TableProps<R, K> & {
    paginated?: boolean,
    pageSizeOptions?: number[],
    defaultPageSize?: number,
    pageSize?: number,
    total?: number,
    currentPage?: number,
    onPageChange?: (page: number, pageSize: number) => void,
    maxButtons?: number,
    rowDisabledHandler?: (rowData: R, index: number) => boolean,
    /**
     * Declarative column definitions. When supplied, the table renders these
     * (all equal-width by default) and `children` is ignored. Define them in a
     * page's utils file rather than hand-writing `<Column>` markup here.
     */
    columns?: TableColumnConfig<R>[]
  }
): JSX.Element {
  const {
    data = [],
    children,
    columns,
    loading = false,
    hover = true,
    fillHeight = false,
    height = 525,
    onRowClick,
    paginated = false, // Pagination is disabled by default
    pageSizeOptions,
    defaultPageSize = DEFAULT_PAGE_SIZE,
    pageSize: controlledPageSize,
    total = data.length, // Default total to the length of the data
    onPageChange, // Callback for page change
    currentPage = 1,
    maxButtons = 3,
    rowClassName,
    rowDisabledHandler,
    renderEmpty,
    ...rest
  } = props

  // Determine initial page size
  const initialPageSize =
    pageSizeOptions && pageSizeOptions.length > 0
      ? pageSizeOptions[0]
      : defaultPageSize

  const [internalPageSize, setInternalPageSize] = useState(initialPageSize)
  const pageSize = controlledPageSize ?? internalPageSize

  // Handle page change
  const handlePageChangeInternal = (page: number) => {
    if (onPageChange) {
      onPageChange(page, pageSize)
    }
  }

  // Handle page size change
  const handlePageSizeChangeInternal = (
    value: number | null,
    _: React.SyntheticEvent
  ) => {
    if (value !== null) {
      if (controlledPageSize === undefined) {
        setInternalPageSize(value)
      }
      if (onPageChange) {
        onPageChange(1, value)
      }
    }
  }

  return (
    <div className="table-container">
      <div className="table-wrapper">
        <RsuiteTable
          loading={loading}
          height={height}
          wordWrap="break-word"
          hover={hover}
          fillHeight={fillHeight}
          data={data}
          onRowClick={onRowClick}
          renderEmpty={renderEmpty || (() => <NoDataFallback />)}
          rowClassName={(rowData, index) => {
            let customRowClass = rowClassName
            if (typeof rowClassName === 'function' && rowData) {
              customRowClass = rowClassName?.(rowData, index)
            }
            return classNames(
              rowDisabledHandler?.(rowData, index) ? 'disabled-table-row' : '',
              customRowClass,
              {
                'clickable-row': !!onRowClick
              }
            )
          }}
          {...rest}
        >
          {columns ? renderTableColumns(columns) : children}
        </RsuiteTable>
      </div>
      {paginated && (
        <TableFooter
          total={total}
          pageSize={pageSize}
          currentPage={currentPage}
          pageSizeOptions={pageSizeOptions}
          onPageChange={handlePageChangeInternal}
          onPageSizeChange={handlePageSizeChangeInternal}
          maxButtons={maxButtons}
        >
          {`${(currentPage - 1) * pageSize + 1}-${Math.min(
            currentPage * pageSize,
            total
          )} of ${total}`}
        </TableFooter>
      )}
    </div>
  )
}

Table.Column = Column
Table.Cell = Cell
Table.HeaderCell = HeaderCell
// Table.CRSCell = CRSCell
Table.StatusCell = StatusCell
Table.ActionCell = ActionCell
Table.CheckCell = CheckCell
Table.CheckHeaderCell = CheckHeaderCell
Table.CustomCell = CustomCell
Table.DEFAULT_PAGE_SIZE = DEFAULT_PAGE_SIZE

export default Table
